import { type Ref, useLayoutEffect } from 'preact/hooks';

// Use a shared ResizeObserver to observe all components using this hook.
// This is much more performant than using one ResizeObserver per element according to this
// benchmark from 2017
// https://groups.google.com/a/chromium.org/g/blink-dev/c/z6ienONUb5A/m/F5-VcUZtBAAJ
//
// A more recent benchmark created in 2023 here https://jsfiddle.net/b6gfdh7k/8/ shows that
// this is still the case across all browsers (though the performance gap is much smaller).

type UseResizeObserverCallback = (entry: ResizeObserverEntry) => void;

class SharedResizeObserver {
  private readonly targetCallbacks: Map<Element, Set<UseResizeObserverCallback>>;
  private readonly resizeObserver: ResizeObserver;

  constructor() {
    this.targetCallbacks = new Map();
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const callbacks = this.targetCallbacks.get(entry.target);
        if (callbacks !== undefined) {
          callbacks.forEach((cb) => cb(entry));
        }
      }
    });
  }

  subscribe(target: Element, callback: UseResizeObserverCallback, options?: ResizeObserverOptions) {
    const callbacks = this.targetCallbacks.get(target);
    if (callbacks === undefined) {
      this.targetCallbacks.set(target, new Set([callback]));
      this.resizeObserver.observe(target, options);
    } else {
      callbacks.add(callback);
    }
  }

  unsubscribe(target: Element, callback: UseResizeObserverCallback) {
    const callbacks = this.targetCallbacks.get(target);
    if (callbacks === undefined) return;

    callbacks.delete(callback);

    if (callbacks.size === 0) {
      this.resizeObserver.unobserve(target);
      this.targetCallbacks.delete(target);
    }
  }
}

const sharedResizeObserver = new SharedResizeObserver();

type UseResizeObserverOptions = {
  /**
   * The ref to the target DOM element to observe.
   */
  ref: Ref<Element>;

  /**
   * A callback function to be called when the size of the observed element changes.
   * If the provided callback triggers the consuming component to re-render, then
   * it's important to pass the same instance of the callback function during each render
   * to prevent infinite re-render loops. One way to achieve this is by using useCallback.
   */
  callback: (entry: ResizeObserverEntry) => void;

  /**
   * Configuration options for the ResizeObserver.
   * If the consuming component re-renders due to resize changes, then
   * it's important to pass the same instance of the options object during each render
   * to prevent infinite re-render loops. One way to achieve this is by using useMemo.
   */
  resizeObserverOptions?: ResizeObserverOptions;
};

/**
 * A hook for observing and responding to changes to the size of a DOM element.
 * It utilizes a ResizeObserver to observe changes to the size of the specified element
 * and accepts a callback function to respond to these changes.
 */
const useResizeObserver = ({ ref, callback, resizeObserverOptions }: UseResizeObserverOptions) => {
  useLayoutEffect(() => {
    // Note ref may be undefined in the hooks storybook demo
    if (!ref?.current) return;

    const target = ref.current;
    sharedResizeObserver.subscribe(target, callback, resizeObserverOptions);

    return () => sharedResizeObserver.unsubscribe(target, callback);
  }, [ref, callback, resizeObserverOptions]);
};

export { useResizeObserver };
